import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AgronodButton } from '@agronod/mui-components';
import type { LockReason } from '@/types';

/** The noun the explanation uses for what is locked. */
const NOUN = {
  tag: { definite: 'taggen', capitalized: 'Taggen' },
  group: { definite: 'typen', capitalized: 'Typen' },
  /** The document's own type: an unlink makes it changeable, never removes it. */
  type: { definite: 'dokumenttypen', capitalized: 'Dokumenttypen' },
} as const;

interface LockExplanationProps {
  /** The links that require the locked tag or tag group. */
  requiredBy: LockReason[];
  /** What is locked: one tag, a further type (removable), or the document's own type (changeable). */
  subject: keyof typeof NOUN;
  /**
   * Called when the farmer chooses to unlink the document from a control that
   * requires the lock. Resolves once the link is gone; the caller refreshes its
   * locks. Without it the panel only explains. Failures are reported by the
   * caller (toast); this component only re-enables its buttons.
   */
  onUnlink?: ((reason: LockReason) => Promise<void>) | undefined;
}

/**
 * The body of a lock explanation popover or sheet: which questions require the
 * locked tag or tag group, and — when `onUnlink` is given — the buttons that
 * unlink the document from the requiring control, the only way to free it.
 * Shared by `TagPicker` (one tag) and `TagSetsEditor` (one tag group) so the two
 * explain a lock identically; only the noun in the last sentences differs.
 */
export default function LockExplanation({ requiredBy, subject, onUnlink }: LockExplanationProps) {
  const noun = NOUN[subject];
  const changeable = subject === 'type';
  /** Control whose unlink is in flight, so its buttons cannot be pressed twice. */
  const [unlinkingControlId, setUnlinkingControlId] = useState<string | null>(null);

  // One unlink removes every link between the control and the document, so a
  // control that holds the lock through two requirements gets one button.
  const unlinkTargets = requiredBy.filter(
    (reason, index, all) => all.findIndex((r) => r.controlId === reason.controlId) === index,
  );

  const handleUnlink = async (reason: LockReason): Promise<void> => {
    if (!onUnlink) return;
    setUnlinkingControlId(reason.controlId);
    try {
      await onUnlink(reason);
    } catch {
      // The caller reports the failure; the panel stays open for a retry.
    } finally {
      setUnlinkingControlId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        {requiredBy.map((reason) => (
          <Typography key={`${reason.controlId}:${reason.requirementId}`} variant="body2">
            Krävs av {reason.requirementDescription} i {reason.controlName}.
          </Typography>
        ))}
        <Typography variant="body2" sx={{ mt: 1 }}>
          {changeable
            ? 'Koppla bort dokumentet från kontrollen för att kunna byta typ.'
            : `Koppla bort dokumentet från kontrollen för att ta bort ${noun.definite}.`}
        </Typography>
      </Box>
      {onUnlink &&
        unlinkTargets.map((reason) => {
          const isLast = unlinkTargets.length === 1;
          return (
            <Box key={reason.controlId}>
              <AgronodButton
                variant="outlined"
                size="small"
                disabled={unlinkingControlId !== null}
                onClick={() => void handleUnlink(reason)}
              >
                {isLast && !changeable
                  ? `Ta bort ${noun.definite} och koppla bort från ${reason.controlName}`
                  : `Koppla bort från ${reason.controlName}`}
              </AgronodButton>
              {!isLast && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {changeable
                    ? 'Typen kan bytas när dokumentet är bortkopplat från alla kontroller som kräver den.'
                    : `${noun.capitalized} kan tas bort när dokumentet är bortkopplat från alla kontroller som kräver den.`}
                </Typography>
              )}
            </Box>
          );
        })}
      {onUnlink && unlinkTargets.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Alla kopplingar mellan dokumentet och kontrollen tas bort direkt, även till frågor i
          kontrollen.{' '}
          {changeable ? 'Därefter kan du byta typ.' : `${noun.capitalized} försvinner från dokumentet när du sparar.`}
        </Typography>
      )}
    </Box>
  );
}
